import axios, { AxiosError } from "axios";
import { SpecialHeader, StateForHeaders } from "@/types/all.types";
import { useIsExpired, useUserState } from "@/states/user.state";
import { useLoadingProgress } from "@/states/ui.state";
import { cookiePersistStorage } from "@/helper/common.helper";

const version: string = process.env.NEXT_PUBLIC_API_VERSION as string;
const baseUrl: string = (process.env.NEXT_PUBLIC_API_URL + version) as string;

export const axiosConfig = {
  baseURL: baseUrl,
  timeout: 100000,
  withCredentials: true,
};

export const getAxiosConfig = (ssrHeaders?: Record<"headers", SpecialHeader>) => {
  const headers = ssrHeaders ?? getHeaders();

  const instance = axios.create({
    ...axiosConfig,
    ...headers,
  });

  instance.interceptors.request.use(
    (config) => {
      const { setLoadingProgress } = useLoadingProgress.getState();

      config.onUploadProgress = (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);

        setLoadingProgress(progress);
      };

      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    async (response) => {
      return response;
    },
    async (error: AxiosError) => {

      const { setParams } = useIsExpired.getState();
      const userState = useUserState.getState();

      if (axios.isCancel(error) || error.message === 'Request aborted') {
        return Promise.reject(error);
      }

      if (error?.response?.status === 401) {
        cookiePersistStorage.removeItem("access_token");
        cookiePersistStorage.removeItem("solutionsSelectedType");
        localStorage.clear();
        window.location.replace("/login");
      } else if (error?.response?.status === 402) {
        setParams({
          isExpired: true,
        });
      } else if (error?.response?.status === 403) {
        if (error?.response?.data.message === "plan-limit") {
          setParams({
            isReach: true,
            isDublicate: false,
          });
        } else if (error?.response?.data.message === "email_verify_required") {
          userState.setEmailVerifyRequired(true);
        }
      } else if (error?.response?.status === 426) {
        setParams({
          isReach: true,
          isDublicate: false,
        });
      } else if (error?.response?.status === 422) {
        if (error?.response?.data?.message === "X-Guest-token not found.") {
          cookiePersistStorage.removeItem("user_state");
        }
      } else if (error?.response?.status === 429) {
        window.location.href = "/429";
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

function convertStateToHeader(state: StateForHeaders): SpecialHeader {
  let { queryParams, url = "/", referer, access_token, guestToken = null, x_lang = "en" } = state;

  return Object.assign(
    {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    queryParams ? { "X-Request-Query": queryParams } : {},
    referer ? { referer } : {},
    { "x-lang": x_lang },
    access_token ? { authorization: `Bearer ${access_token}` } : {},
    {
      "X-Link":
        typeof window !== "undefined"
          ? new URL(window.location.href)?.pathname
          : url
            ? url.split("?")?.[0]
            : "",
      "X-Guest-Token": guestToken,
    },
  );
}

export function getHeaders(): Record<"headers", SpecialHeader> {
  const access_token = cookiePersistStorage.getItem("access_token");
  const user_state = useUserState.getState();
  const next_locale = cookiePersistStorage.getItem("NEXT_LOCALE");

  const state = {
    ...user_state,
    x_lang: next_locale || "en",
    access_token,
    guestToken: user_state.guestToken,
  };

  return { headers: convertStateToHeader(state as StateForHeaders) };
}

export function getHeadersForSSR(state: StateForHeaders): Record<"headers", SpecialHeader> {
  return { headers: convertStateToHeader(state) };
}
